import { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { ConfigContext } from '../shared/ConfigContext';
import {
  fetchPublicPlans, fetchMyEntitlements, changeCompanySubscription, startCheckout, openBillingPortal,
  cancelPendingPlanChange, cancelSubscription, resumeSubscription,
} from '../../api/planClient';
import eventBus from '../../utils/eventBus';
import navigation from '../../utils/navigation';

const mapError = (t, err) => {
  if (err.response?.status === 403) return err.response?.data?.error || t('ga.error.forbidden');
  if (err.response?.status === 503) return err.response?.data?.error || t('Online payments are not available yet — contact us to subscribe.', { defaultValue: 'Online payments are not available yet — contact us to subscribe.' });
  if (err.response?.status === 400 || err.response?.status === 404 || err.response?.status === 409) return err.response?.data?.error || t('ga.error.generic');
  if (err.response?.status >= 500) return t('ga.error.server');
  if (!err.response) return t('ga.error.network');
  return t('ga.error.generic');
};

// Which kind of change is "switch to this plan" for the current company?
//   checkout  – no Stripe subscription yet: go through Stripe Checkout
//   upgrade   – paid now (prorated), same renewal date
//   downgrade – scheduled for the end of the current period
export const changeModeFor = (plan, currentPlan, isStripe) => {
  if (!isStripe || !currentPlan) return 'checkout';
  return (plan.price?.amount ?? 0) >= (currentPlan.price?.amount ?? 0) ? 'upgrade' : 'downgrade';
};

const useSubscriptionPage = () => {
  const { t } = useTranslation();
  const config = useContext(ConfigContext);
  const location = useLocation();
  const navigate = useNavigate();
  const companyId = localStorage.getItem('companyId') || '';
  const isAdmin = localStorage.getItem('userRole') === 'admin';

  const [status, setStatus] = useState('loading');
  const [errorInfo, setErrorInfo] = useState(null);
  const [plans, setPlans] = useState([]);
  const [billing, setBilling] = useState({ stripeEnabled: false, currency: 'CAD', graceDays: 3 });
  const [entitlements, setEntitlements] = useState(null);

  const [confirm, setConfirm] = useState(null);   // { plan, mode }
  const [overage, setOverage] = useState(null);   // server's OVER_LIMIT payload
  const [cancelOpen, setCancelOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = useCallback((message, severity = 'success') => setSnackbar({ open: true, message, severity }), []);

  const load = useCallback(() => {
    if (!config?.backendUrl) return;
    setStatus('loading');
    Promise.all([fetchPublicPlans(config.backendUrl), fetchMyEntitlements(config.backendUrl)])
      .then(([p, e]) => {
        setPlans(p.data.items);
        setBilling({ stripeEnabled: false, currency: 'CAD', graceDays: 3, ...(p.data.billing || {}) });
        setEntitlements(e.data);
        setStatus('ready');
      })
      .catch((error) => {
        console.error('Error loading subscription', error);
        if (error.response?.status === 401 || error.response?.status === 403) setStatus('permission');
        else {
          setErrorInfo({ request: `GET /plans · ${error.response?.status || t('N/A')} · ${new Date().toLocaleTimeString()}` });
          setStatus('error');
        }
      });
  }, [config, t]);

  useEffect(() => { load(); }, [load]);

  // Back from Stripe Checkout: the webhook that activates the plan may land a
  // moment after the redirect, so re-read a couple of times.
  const handledReturn = useRef(false);
  useEffect(() => {
    if (handledReturn.current) return;
    const params = new URLSearchParams(location.search);
    const outcome = params.get('checkout');
    if (!outcome) return;
    handledReturn.current = true;
    if (outcome === 'success') {
      notify(t('Payment received — activating your plan…', { defaultValue: 'Payment received — activating your plan…' }));
      const timers = [2500, 6000].map((ms) => setTimeout(() => { load(); eventBus.emit(eventBus.EVENTS.ENTITLEMENTS_REFRESH); }, ms));
      navigate('/subscription', { replace: true });
      return () => timers.forEach(clearTimeout);
    }
    notify(t('Checkout was canceled — nothing was charged.', { defaultValue: 'Checkout was canceled — nothing was charged.' }), 'info');
    navigate('/subscription', { replace: true });
    return undefined;
  }, [location.search, load, navigate, notify, t]);

  const currentPlan = entitlements?.plan || null;
  const subscription = entitlements?.subscription || null;
  const subscribed = Boolean(entitlements?.subscribed);
  const managed = Boolean(subscription?.managed);
  const isStripe = Boolean(subscription?.stripe);
  const pastDue = subscription?.status === 'past_due';
  const meters = useMemo(() => Object.entries(entitlements?.features || {})
    .filter(([, v]) => v.kind !== 'boolean' && v.enabled && v.used != null)
    .map(([key, v]) => ({ key, ...v })), [entitlements]);

  const canAct = isAdmin && billing.stripeEnabled && !managed;

  const run = async (fn, successMessage) => {
    setBusy(true);
    try {
      const result = await fn();
      if (successMessage) notify(successMessage);
      eventBus.emit(eventBus.EVENTS.ENTITLEMENTS_REFRESH);
      load();
      return result;
    } catch (error) {
      notify(mapError(t, error), 'error');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const startSwitch = (plan) => {
    setOverage(null);
    setConfirm({ plan, mode: changeModeFor(plan, currentPlan, isStripe) });
  };

  const performSwitch = async (acknowledge = false) => {
    if (!confirm || busy) return;
    const { plan, mode } = confirm;
    setBusy(true);
    try {
      if (mode === 'checkout') {
        const res = await startCheckout(config.backendUrl, companyId, plan._id);
        navigation.redirectTo(res.data.url);
        return;
      }
      const res = await changeCompanySubscription(config.backendUrl, companyId, plan._id, acknowledge);
      const effectiveAt = res.data.effectiveAt ? new Date(res.data.effectiveAt).toLocaleDateString() : '';
      notify(res.data.effective === 'now'
        ? t('Your company is now on {{plan}}', { plan: plan.name, defaultValue: `Your company is now on ${plan.name}` })
        : t('Your company moves to {{plan}} on {{date}}', { plan: plan.name, date: effectiveAt, defaultValue: `Your company moves to ${plan.name} on ${effectiveAt}` }));
      setConfirm(null);
      setOverage(null);
      eventBus.emit(eventBus.EVENTS.ENTITLEMENTS_REFRESH);
      load();
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.code === 'OVER_LIMIT') {
        setOverage(error.response.data.overLimit);
      } else {
        notify(mapError(t, error), 'error');
        setConfirm(null);
      }
    } finally {
      setBusy(false);
    }
  };

  // The portal opens in a new tab so the admin keeps this page; the tab is
  // opened before the request so the browser treats it as user-initiated.
  const openPortal = async () => {
    const tab = navigation.openBlankTab();
    setBusy(true);
    try {
      const res = await openBillingPortal(config.backendUrl, companyId);
      navigation.pointTab(tab, res.data.url);
    } catch (error) {
      navigation.closeTab(tab);
      notify(mapError(t, error), 'error');
    } finally {
      setBusy(false);
    }
  };

  const keepCurrentPlan = () => run(
    () => cancelPendingPlanChange(config.backendUrl, companyId),
    t('Scheduled change removed — you stay on {{plan}}', { plan: currentPlan?.name, defaultValue: `Scheduled change removed — you stay on ${currentPlan?.name}` }),
  );
  const cancelPlan = async () => {
    const ok = await run(() => cancelSubscription(config.backendUrl, companyId),
      t('Your subscription ends at the end of this billing period', { defaultValue: 'Your subscription ends at the end of this billing period' }));
    if (ok) setCancelOpen(false);
  };
  const resumePlan = () => run(() => resumeSubscription(config.backendUrl, companyId),
    t('Your subscription will renew as usual', { defaultValue: 'Your subscription will renew as usual' }));

  return {
    t, status, errorInfo, load, isAdmin, billing, plans, currentPlan, entitlements, meters,
    subscription, subscribed, managed, isStripe, pastDue, canAct,
    confirm, setConfirm, overage, startSwitch, performSwitch, busy,
    openPortal, keepCurrentPlan, cancelOpen, setCancelOpen, cancelPlan, resumePlan,
    snackbar, setSnackbar,
  };
};

export default useSubscriptionPage;
